<template>
  <router-link
    class="tile"
    :style="`background-image: url(${background}), linear-gradient(${$getGradient()}); height: ${height}px;`"
    :to="link"
    @click.native="savePosition"
  >
    <div :id="item.slug" class="tile__overlay"></div>
    <div class="tile__tile">
      <div v-if="showHeader" class="tile__tile-header">
        Live Bootcamp
        <span v-if="isContentComingSoon" class="tile__tile-header--status"
          >Coming Soon</span
        >
      </div>
      <div
        :class="[
          'tile__tile-body',
          showHeader && 'tile__tile-body--with-header',
        ]"
      >
        <div class="tile__tile-top">
          <div class="tile__pills-container">
            <template v-if="displayRunningPill && running">
              <div class="tile__running-pill">
                <RunningPill />
              </div>
            </template>
            <span v-if="registered" class="tile__registered"
              >You registered!</span
            >
            <div
              v-if="item.published_date && showAddedDate"
              class="tile__added-date__wrapper"
            >
              <AddedDate :date="item.published_date" :limit="7"></AddedDate>
            </div>
          </div>
          <div class="tile__snipes-wrapper">
            <p v-if="isResumeSnipeShown" class="tile__snipe">Resume</p>
            <p
              v-if="item.has_sample_content && isFreeTrialUser"
              class="tile__snipe"
            >
              Included
            </p>
            <p v-if="isComingSoon" class="tile__snipe">Coming Soon</p>
          </div>
          <div
            v-if="height >= tileSizes.medium || isDifficultyShown"
            class="tile__difficulty"
          >
            <Difficulty
              v-if="item.difficulty && item.difficulty.length"
              :difficulty="item.difficulty || ''"
              :alwaysVisible="isDifficultyShown"
            />
          </div>
          <p
            v-tooltip="{
              content: item.name.length >= maxLength ? item.name : '',
            }"
            :class="['tile__name', longItemName(item) && 'tile__name--toggle']"
          >
            {{ truncateTitle(item.name) }}
          </p>
          <p
            v-tooltip="{
              content: item.name.length >= maxLength ? item.name : '',
            }"
            :class="[
              'tile__name--short',
              longItemName(item) && 'tile__name--toggle',
            ]"
          >
            {{ truncateTitle(item.name, 65) }}
          </p>
          <div
            v-if="
              showLastActivity() ||
              item.duration_in_seconds ||
              item.duration ||
              (displayCertificate && getCertificateName(item))
            "
            class="tile__statistics-wrapper"
          >
            <div
              v-if="displayCertificate && getCertificateName(item)"
              v-tooltip="{
                content: `${getCertificationFullName(item)}`,
                placement: 'top-center',
              }"
              class="tile__certificate"
            >
              <div
                class="tile__certificate--icon"
                :style="`background: ${learningAreaColor}`"
              >
                <CertificateIcon />
              </div>
              <span> {{ getCertificateName(item) }} </span>
            </div>
            <div v-if="displayDuration" class="tile__item-length">
              {{ itemDuration(item) }}
            </div>
            <span
              v-if="showLastActivity() && item.duration_in_seconds"
              class="tile__statistics-separator"
              >|</span
            >
            <div v-if="showLastActivity()" class="tile__item-last-activity">
              Active {{ getTimeInDaysAgo() }}
            </div>
            <div v-if="displayLabType">
              <span>{{ getLabTypeLabel(item) }}</span>
            </div>

            <div v-if="displayParentCourse">
              <span>COURSE: {{ truncateTitle(courseName) }}</span>
            </div>
          </div>
        </div>
        <div class="tile__tile-bottom">
          <div
            :class="[
              'tile__info-wrapper',
              (!displayVendor || !displayInstructor) && 'tile__align-end',
              !displayVendor && !displayInstructor && 'tile__justify-end',
            ]"
          >
            <template v-if="displayInstructor">
              <div class="tile__instructor-wrapper">
                <img
                  v-if="height >= tileSizes.large || isInstructorPictureShown"
                  class="tile__instructor-img"
                  :src="getInstructorImage(item)"
                  :alt="getInstructorNameText(item)"
                />
                <p class="tile__instructor-name">
                  {{ getInstructorNameText(item) }}
                </p>
              </div>
            </template>
            <div
              :style="`--la: ${learningAreaColor}`"
              class="tile__vendor-wrapper"
            >
              <p class="tile__learning-area">
                {{ learningArea }}
              </p>
              <p class="tile__vendor-name">
                <span v-if="displayVendor">{{ getVendor(item) }}</span>
              </p>
            </div>
          </div>
          <div v-if="itemProgress" class="tile__progress-wrapper">
            <PreviewProgressBar :value="itemProgress"></PreviewProgressBar>
          </div>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { mapState } from 'vuex'
import CONSTANTS from '@/constants'
import { defineComponent } from '@vue/composition-api'
import { differenceInDays, formatDistanceToNow } from 'date-fns'
import {
  truncateText,
  getEntities,
  getInstructorImg,
  getCertificateName,
  getCertificationFullName,
  saveCurrentElement,
} from '@/helpers'
import Difficulty from '@/components/properties/Difficulty.vue'
import PreviewProgressBar from '@/components/properties/PreviewProgressBar.vue'
import AddedDate from '@/components/properties/AddedDate.vue'
import CertificateIcon from '@/assets/icons/search/certificate.svg'
import RunningPill from '@/components/properties/RunningPill.vue'

export default defineComponent({
  name: 'Tile',
  components: {
    Difficulty,
    PreviewProgressBar,
    AddedDate,
    CertificateIcon,
    RunningPill,
  },
  data: () => {
    return {
      getCertificateName,
      getCertificationFullName,
    }
  },
  computed: {
    ...mapState({
      searchUserStatusBootcamps: state =>
        state.bootcamps.searchUserStatusBootcamps,
    }),
    isComingSoon() {
      const comingSoonKey = CONSTANTS.CONTENT_STATUS.COMING_SOON.replace(
        /_/g,
        ' '
      )
      return this.item.status === comingSoonKey
    },
    isContentComingSoon() {
      const contentComingSoonKey =
        CONSTANTS.BOOTCAMP_CONTENT_STATUS.CONTENT_COMING_SOON.replace(/_/g, ' ')
      return this.item.status
        ? this.item.status === contentComingSoonKey
        : false
    },
    addedInDays() {
      const publishedDate = this.item.published_date * 1000
      const publishedSince = differenceInDays(
        new Date(),
        new Date(publishedDate)
      )
      return publishedSince
    },
    maxLength() {
      return 90
    },
    tileSizes() {
      return {
        small: 286,
        medium: 386,
        large: 490,
      }
    },
    userStatus() {
      const itemStatus = this.searchUserStatusBootcamps?.find(
        e => e.id === this.item.objectID
      )
      return itemStatus?.user_status
    },
    registered() {
      return this.userStatus?.registered
    },
    itemProgress() {
      if (this.progress) return this.progress
      let progress = 0
      if (this.userStatus && this.userStatus.progress) {
        progress = Math.floor(this.userStatus.progress * 100)
      }
      return progress
    },
    lastActivity() {
      if (
        this.item?.last_activity_date &&
        this.item?.last_activity_date !== 'None'
      ) {
        return this.item.last_activity_date
      }
      return this.userStatus?.last_activity_date || null
    },
  },
  props: {
    link: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    displayAllInstructors: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    displayVendor: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    background: {
      type: String,
      required: false,
    },
    item: {
      type: Object,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
    height: {
      type: [String, Number],
      required: false,
      default: () => 245,
    },
    isResumeSnipeShown: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isDifficultyShown: {
      type: Boolean,
      required: false,
    },
    isInstructorPictureShown: {
      type: Boolean,
      required: false,
    },
    progress: {
      type: Number,
      required: false,
      default: () => 0,
    },
    showAddedDate: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    learningArea: {
      type: String,
      required: false,
      default: () => null,
    },
    learningAreaColor: {
      type: String,
      required: false,
      default: () => null,
    },
    displayCertificate: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    displayDuration: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    displayRunningPill: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    running: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    displayLabType: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    displayInstructor: {
      type: Boolean,
      required: false,
      default: () => true,
    },
    showHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
    displayParentCourse: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    courseName: {
      type: String,
      required: false,
      default: () => '',
    },
  },
  mounted() {
    this.$emit('element-rend', this.item.slug)
  },
  methods: {
    longItemName(item) {
      return this.itemHasHeaderLabel(item) && item.name.length > 59
    },
    itemHasHeaderLabel(item) {
      return (
        this.isResumeSnipeShown ||
        (item.has_sample_content && this.isFreeTrialUser) ||
        this.isComingSoon
      )
    },
    truncateTitle(value, length = null) {
      return truncateText(value, !length ? this.maxLength.value : length)
    },
    showLastActivity() {
      return this.lastActivity && this.lastActivity !== 'None'
    },
    itemDuration(item = {}) {
      const { duration, duration_in_seconds } = item
      const time = duration || duration_in_seconds
      // Keep this check until all indexes has been updated to new global index
      // duration and duration_in_seconds has been unified in new index
      if (time) return this.$duration.durationFromSecs(time)

      return null
    },
    getTimeInDaysAgo() {
      const time = this.lastActivity ?? ''
      return time && time !== 'None'
        ? `${formatDistanceToNow(new Date(time))} ago`
        : ''
    },
    getVendor(item) {
      return getEntities.getVendorName(item)
    },
    getInstructorImage(item) {
      const url =
        item?.instructors[0]?.profile_img_url ??
        'https://s3.amazonaws.com/ine-vegas/production/images/instructors/INE-Instructor.png'
      return getInstructorImg(url)
    },
    getInstructorNameText(item) {
      if (!this.displayAllInstructors) {
        return getEntities.getInstructorName(item)
      }
      let text = getEntities.getInstructorName(item)
      const amountOfInstructors = getEntities.getAmountOfInstructors(item)
      if (amountOfInstructors > 1) {
        text += ` +${amountOfInstructors}`
      }
      return text
    },
    getLabTypeLabel(item) {
      if (!item.lab_type) {
        return null
      }
      const types = {
        azure: 'Azure',
        github: 'GitHub',
        els: 'eLearningSecurity',
        pta: 'Pentester Academy',
      }
      return types[item.lab_type]
    },
    savePosition() {
      saveCurrentElement(this.item.slug)
    },
  },
})
</script>
